import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { defaultLanguage, SupportedLanguage } from "./config";

const PREFIX_RE = /^\/(en|de)(?=\/|$)/;

/** Public origin, used only for the absolute hreflang URLs. */
export const SITE_ORIGIN = "https://svenbadalyan.com";

/** Remove a leading `/en` or `/de` segment; always returns a path starting `/`. */
export const stripLocalePrefix = (pathname: string): string => {
  const stripped = pathname.replace(PREFIX_RE, "");
  return stripped === "" ? "/" : stripped;
};

/** Language implied by the URL. Anything without an `/en` or `/de` prefix is
 *  the default language (English), so existing English URLs are unchanged. */
export const getLocaleFromPath = (pathname: string): SupportedLanguage => {
  const match = pathname.match(PREFIX_RE);
  return (match?.[1] as SupportedLanguage) ?? defaultLanguage;
};

/** Build an app path for `routePath` in `lang`. English is canonical at the
 *  root (no prefix); German lives under `/de`. */
export const localizedHref = (
  lang: SupportedLanguage,
  routePath: string,
): string => {
  const clean = routePath.startsWith("/") ? routePath : `/${routePath}`;
  if (lang === defaultLanguage) return clean;
  const base = `/${lang}`;
  return clean === "/" ? `${base}/` : `${base}${clean}`;
};

export const useLocale = () => {
  const { pathname } = useLocation();

  const language = getLocaleFromPath(pathname);
  const subPath = stripLocalePrefix(pathname);

  const toRoute = useCallback(
    (routePath: string) => localizedHref(language, routePath),
    [language],
  );

  const hrefForLanguage = useCallback(
    (lang: SupportedLanguage) => localizedHref(lang, subPath),
    [subPath],
  );

  return { language, subPath, toRoute, hrefForLanguage };
};

/** Keep `<link rel="alternate" hreflang>` tags in sync with the current route.
 *  This is a client-only SPA, so the tags are managed imperatively. */
export const useHreflangTags = () => {
  const { subPath } = useLocale();

  useEffect(() => {
    const MARK = "data-i18n-alt";
    const path = subPath === "/" ? "/" : subPath;
    const entries: Array<[string, string]> = [
      ["en", `${SITE_ORIGIN}${path}`],
      ["de", `${SITE_ORIGIN}/de${path}`],
      ["x-default", `${SITE_ORIGIN}${path}`],
    ];

    // Remove every existing hreflang link, not just the ones this hook
    // created: scripts/prerender-meta.js bakes a static set (without MARK)
    // into each prerendered index.html for non-JS crawlers, and this hook
    // takes ownership of them once the app is running.
    document.head
      .querySelectorAll(`link[${MARK}], link[rel="alternate"][hreflang]`)
      .forEach((node) => node.remove());

    entries.forEach(([hreflang, href]) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute("href", href);
      link.setAttribute(MARK, "");
      document.head.appendChild(link);
    });

    return () => {
      document.head
        .querySelectorAll(`link[${MARK}]`)
        .forEach((node) => node.remove());
    };
  }, [subPath]);
};
